import { api } from '@/api/api'
import { endpoints } from '@/api/endpoints'
import {
  SendPhoneVerificationCodePayload,
  SendPhoneVerificationCodeDTO,
  VerifyPhoneUpdatePayload,
  VerifyPhoneUpdateDTO,
  ResendVerificationEmailPayload,
  UserProfile,
  ProfileDTO,
  UpdateProfilePayload,
  UpdateProfileDTO,
  RelationshipParams,
  IdentityVerificationDTO,
  UploadAvatarPayload,
  UploadAvatarDTO,
  UpdatePasswordPayload,
  ReportUserPayload,
  ReportUserDTO,
  GetBlockedUserListPayload,
  GetBlockedUserListDTO,
  UpdateUserKycInfoPayload,
  UpdateUserKycInfoDTO,
  CardIssueDTO,
  GetKycSdkAccessTokenDTO,
  GetDinariKycStatusDTO,
} from './identityTypes'

// ======== PHONE MANAGEMENT ==============
async function sendPhoneVerificationCode(
  payload: SendPhoneVerificationCodePayload,
): Promise<SendPhoneVerificationCodeDTO> {
  const res = await api.post(
    endpoints.identity.sendPhoneVerificationCode,
    payload,
  )
  return res.data
}

async function verifyPhoneUpdate(
  payload: VerifyPhoneUpdatePayload,
): Promise<VerifyPhoneUpdateDTO> {
  const res = await api.post(endpoints.identity.verifyPhoneUpdate, payload)
  return res.data
}

// ======== USER PROFILE ==============
async function getMe(): Promise<UserProfile> {
  const res = await api.get(endpoints.identity.me)
  return res.data
}

async function getUserProfile(userId: string): Promise<ProfileDTO> {
  const res = await api.get(endpoints.identity.getProfile(userId))
  return res.data
}

// ======== PROFILE MANAGEMENT ==============
async function updateProfile(
  payload: UpdateProfilePayload,
): Promise<UpdateProfileDTO> {
  const res = await api.patch(endpoints.identity.updateProfile, {
    first_name: payload?.first_name,
    last_name: payload?.last_name,
    username: payload?.username,
  })
  return res.data
}

async function updateUserKycInfo(
  payload: UpdateUserKycInfoPayload,
): Promise<UpdateUserKycInfoDTO> {
  const res = await api.post(endpoints.identity.kycInfo, {
    first_name: payload?.first_name,
    last_name: payload?.last_name,
  })
  return res.data
}

// ======== AVATAR UPLOAD ==============
async function uploadAvatar(
  payload: UploadAvatarPayload,
): Promise<UploadAvatarDTO> {
  const formData = new FormData()
  formData.append('file', payload.file)

  const res = await api.post(endpoints.identity.uploadAvatar, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return res.data
}

// ======== RELATIONSHIP MANAGEMENT ==============
async function addUserToFavorites({
  userId,
}: RelationshipParams): Promise<void> {
  const res = await api.post(endpoints.identity.addUserToFavorites(userId))
  return res.data
}

async function unfavoriteUser({ userId }: RelationshipParams): Promise<void> {
  const res = await api.delete(endpoints.identity.unfavoriteUser(userId))
  return res.data
}

// ======== IDENTITY VERIFICATION ==============
async function identityVerification(): Promise<IdentityVerificationDTO> {
  const res = await api.post(endpoints.identity.kyc)
  return res.data
}

async function getKycSdkAccessToken(): Promise<GetKycSdkAccessTokenDTO> {
  const res = await api.get(endpoints.identity.kycSDKAccessToken)
  return res.data
}

// ======== PASSWORD UPDATE ==============
async function updatePassword(payload: UpdatePasswordPayload): Promise<void> {
  const res = await api.post(endpoints.identity.updatePassword, {
    old_password: payload.oldPassword,
    new_password: payload.newPassword,
  })

  return res.data
}

// ======== BLOCK/UNBLOCK USERS ==============
async function blockUser({ userId }: RelationshipParams): Promise<void> {
  const res = await api.post(endpoints.identity.block(userId))
  return res.data
}

async function unblockUser({ userId }: RelationshipParams): Promise<void> {
  const res = await api.delete(endpoints.identity.unblock(userId))
  return res.data
}

// ======== REPORT USER ==============
async function reportUser(payload: ReportUserPayload): Promise<void> {
  const res = await api.post(endpoints.identity.reportsUser(payload.userId), {
    message: payload.message,
  })
  return res.data
}

// ======== BLOCKED USERS ==============
async function getBlockedUserList({
  userID,
}: GetBlockedUserListPayload): Promise<GetBlockedUserListDTO> {
  const res = await api.get(endpoints.identity.blockedUsersList(userID))
  return res.data
}

// ======== EMAIL VERIFICATION ==============
async function resendVerificationEmail(
  payload: ResendVerificationEmailPayload,
): Promise<void> {
  const res = await api.post(
    endpoints.identity.resendVerificationEmail,
    payload,
  )
  return res.data
}

// ======== CREATE KEM CARD ==============
async function createKemCard(): Promise<CardIssueDTO> {
  const res = await api.post(endpoints.identity.createKemCard)
  return res.data
}

// ======== DINARI KYC ==============
async function getDinariKycStatus(): Promise<GetDinariKycStatusDTO> {
  const res = await api.get(endpoints.identity.dinariKycStatus)
  return res.data
}

async function startDinariKyc(): Promise<GetDinariKycStatusDTO> {
  const res = await api.post(endpoints.identity.dinariStartKyc)
  return res.data
}

// ======== REQUEST USER DATA ==============
async function requestYourData(): Promise<void> {
  const res = await api.post(endpoints.identity.requestYourData)
  return res.data
}

export const identityApi = {
  sendPhoneVerificationCode,
  verifyPhoneUpdate,
  getMe,
  getUserProfile,
  updateProfile,
  updateUserKycInfo,
  uploadAvatar,
  addUserToFavorites,
  unfavoriteUser,
  blockUser,
  unblockUser,
  reportUser,
  identityVerification,
  getKycSdkAccessToken,
  updatePassword,
  getBlockedUserList,
  resendVerificationEmail,
  createKemCard,
  getDinariKycStatus,
  startDinariKyc,
  requestYourData,
}
